import { useMemo } from 'react';
import { CountriesEnum } from '@/enums/countries';

/**
 * Resolves the locale entry that matches the user's geo-located country.
 * Returns `null` when the user is already viewing in the correct locale,
 * or when no geo-country is available yet.
 *
 * Falls back to the English locale when the country is not in `CountriesEnum`.
 *
 * @param {string | null} geoCountry - Two-letter country code from geo-location (e.g. "NL").
 * @param {string} currentLocale - The locale of the page being viewed (e.g. "nl").
 * @returns {import('@/enums/countries').Country | null} The suggested country entry, or `null` if no switch is needed.
 */
export function useLocaleSuggestion(geoCountry, currentLocale) {
	return useMemo(() => {
		if (!geoCountry) {
			return null;
		}

		const expected =
			CountriesEnum.find(c => c.countryCode === geoCountry) || CountriesEnum.find(c => c.locale === 'en');

		if (expected.locale === currentLocale) {
			return null;
		}

		return expected;
	}, [geoCountry, currentLocale]);
}
